/**
 * `PageLifecycle` — whether the page this code runs in is being unloaded,
 * and the report of a failed request that tells an abort caused by that
 * unload apart from a real failure.
 *
 * When a page navigates away or reloads, the browser aborts every request
 * the old document still has in flight: each one rejects as unanswered
 * ("Failed to fetch") during the old document's teardown, a few
 * milliseconds BEFORE its `pagehide` is dispatched. Such a request did not
 * fail; its page left. `reportUnlessLeaving` therefore defers the report of
 * a transport failure by one macrotask, and holds it longer while an unload
 * is pending: after a `beforeunload` nobody cancelled, for up to
 * `UNLOAD_PENDING_MS`. If `pagehide` fires by then (or the document is gone
 * and no timer runs), the failure is recorded at info as
 * `sdk-request-abandoned` instead of reported. A `beforeunload` that does
 * not unload (a download, a 204, `mailto:`, Stop) is followed by no
 * `pagehide`: the held failure is reported when the window ends, late but
 * not lost. Only a failure the transport classified as unanswered counts
 * (an `SdkRequestError` with outcome `"unanswered"`); a refused or malformed
 * answer and any other error (a code bug, a `TypeError` included) are
 * reported synchronously, whatever the page is doing.
 *
 * One instance per page, owned by whatever already lives as long as the page
 * (the SDK client, which hands it to a host as `platformOf(client).page`;
 * published on `@tento/lona/host`): `new PageLifecycle(globalThis)`
 * starts watching the target's lifecycle events at construction. Where
 * there is no page (Deno, a worker) it is built over `null` — or over a
 * target that never dispatches them — and `leaving()` stays false, so every
 * failure is reported.
 */
import { Tracing } from "@tento-core/tracing";
import type { Option } from "@tento-lona/sheets";
import { SdkRequestError } from "@tento-lona/sheets";

/** How long after a `beforeunload` nobody cancelled an unload is pending:
 * a transport failure in that window is held until `pagehide` (abandoned) or
 * the window's end (reported). Browsers dispatch `pagehide` within a few
 * milliseconds of aborting the old document's requests; this is generous. */
export const UNLOAD_PENDING_MS = 300;

/** A failure of the transport itself: the request got no answer. The
 *  transport turns `fetch`'s own rejection into that outcome; a bare
 *  `TypeError` is a code bug, not a transport failure. */
function isTransportFailure(err: Error): boolean {
  return err instanceof SdkRequestError && err.outcome === "unanswered";
}

/**
 * Whether one page is being unloaded, and the report of a failed request
 * that records an abort caused by that unload at info instead of as an
 * error (`reportUnlessLeaving`).
 */
export class PageLifecycle {
  #hidden = false;
  /** The pending unload: the `beforeunload` nobody had cancelled yet, its
   *  window's end timer, and the transport failures held until either. */
  #pending: Option<{
    readonly event: Event;
    readonly timer: ReturnType<typeof setTimeout>;
    readonly held: { readonly err: Error; readonly report: () => void }[];
  }> = null;

  /**
   * Watch `target`'s `pagehide` / `pageshow` / `beforeunload` from now on;
   * `null` (no page) never leaves.
   */
  constructor(target: Option<EventTarget>) {
    // Its state is in private fields, so a frozen instance still works, and
    // one handed out (`platformOf(client).page`) cannot be rewritten.
    Object.freeze(this);
    if (target == null) return;
    target.addEventListener("pagehide", () => {
      this.#hidden = true;
      for (const { err } of this.#endPending()) abandoned(err);
    });
    target.addEventListener("pageshow", () => {
      this.#hidden = false;
      for (const { report } of this.#endPending()) report();
    });
    target.addEventListener("beforeunload", (event) => {
      for (const { report } of this.#endPending()) report();
      const held: { readonly err: Error; readonly report: () => void }[] = [];
      const timer = setTimeout(() => {
        for (const { report } of this.#endPending()) report();
      }, UNLOAD_PENDING_MS);
      this.#pending = { event, timer, held };
    });
  }

  /**
   * Whether the page is being unloaded: `pagehide` fired and no `pageshow`
   * (a back/forward-cache restore) since. A `beforeunload` alone does not
   * decide it: it may be cancelled, or followed by no unload at all. Always
   * false where there is no page.
   */
  leaving(): boolean {
    return this.#hidden;
  }

  /**
   * Report `err` through `report`, unless it is a transport failure of a
   * page that is leaving. A `catch` site narrows what it caught to an
   * `Error` first (`err instanceof Error ? err : new Error(String(err))`).
   * A transport failure (`isTransportFailure`) is judged one macrotask
   * later: on a hidden page, `report` is skipped and an info record
   * `sdk-request-abandoned` is written instead; while an unload is pending
   * (an uncancelled `beforeunload` within `UNLOAD_PENDING_MS`) it is held
   * until `pagehide` (abandoned) or the window's end, `pageshow` or a new
   * `beforeunload` (reported); otherwise `report` runs. Any other error
   * calls `report` synchronously. `report` may run from a timer or an event
   * listener, so it must not throw: the callers are fire-and-forget logs.
   *
   * The macrotask holds even when no unload is pending: a navigation can
   * abort the old document's requests after the `beforeunload` window has
   * closed (a slow response) or with no `beforeunload` at all, and its
   * `pagehide` arrives just after the aborts. So a transport failure's
   * report may land after a synchronous report its caller made later; each
   * record carries its own time.
   */
  reportUnlessLeaving(err: Error, report: () => void): void {
    if (!isTransportFailure(err)) {
      report();
      return;
    }
    const judge = () => {
      if (this.#hidden) abandoned(err);
      else if (this.#unloadPending()) this.#pending?.held.push({ err, report });
      else report();
    };
    if (this.#hidden) judge();
    else setTimeout(judge, 0);
  }

  /** Whether an uncancelled `beforeunload`'s window is open. Read after the
   *  dispatch finished: a later listener may have cancelled the unload
   *  (`preventDefault`, or a non-empty legacy `returnValue`); a cancelled one
   *  releases what it held. */
  #unloadPending(): boolean {
    const pending = this.#pending;
    if (pending == null) return false;
    const returnValue: unknown = Reflect.get(pending.event, "returnValue");
    const cancelled = pending.event.defaultPrevented ||
      (typeof returnValue === "string" && returnValue.length > 0);
    if (!cancelled) return true;
    for (const { report } of this.#endPending()) report();
    return false;
  }

  /** Close the pending window, if any; return what it held. */
  #endPending(): readonly { readonly err: Error; readonly report: () => void }[] {
    const pending = this.#pending;
    if (pending == null) return [];
    this.#pending = null;
    clearTimeout(pending.timer);
    return pending.held;
  }
}

function abandoned(err: Error): void {
  const request = err instanceof SdkRequestError ? err : null;
  Tracing.log("sdk-request-abandoned", {
    boundary: request?.boundary ?? null,
    step: request?.step ?? null,
    path: request?.path ?? null,
    reason: "page-unload",
  });
}
